import React, { useContext } from "react";
import {
    GelContainerLite,
    GelButton,
    GelHeading2,
    GelHeading5,
    GelParagraph,
    GelRowLayout,
    GelScreenDetectorContext,
    GelLabel,
    GelSpinner
} from "@tal-gel/components";
import { getGelTokens } from "@tal-gel/theming";
import { Attachment, CorroLinkFileResult } from "../CorroLinkModels";
import styled from "@emotion/styled";
import ResponsiveContainer from "../../../components/responsive.container";
import Card from "../../../common/components/card";
import { CorroFileItem } from "./CorroFileItem";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { useGetHomePageContent } from "../../../common/api/graphQLDataFetch";
import InlineAlert from "../../../common/components/InlineAlert";
import HorizontalRule from "../../../common/components/HorizontalRule";
import PageHeader from "../../../common/components/PageHeader";

interface Props {
    fileResult: CorroLinkFileResult;
}

const CorroAttachmentList: React.FC<Props> = ({ fileResult }) => {
    const { attachments, createdAt, policyDetails, policyNumber, claimNumber, lifeInsured } = fileResult;

    const { data, isLoading } = useGetHomePageContent()

    type Detail = { label: string; value?: React.ReactNode };
    const details: Detail[] = [
        { label: "Policy", value: policyDetails },
        { label: "Policy number", value: policyNumber },
        { label: "Claim number", value: claimNumber },
        { label: "Life insured", value: lifeInsured },
    ];

    /* ── design tokens / screen helpers ─────────────────────────────── */
    const {
        global: { sizeNone, sizeBaseX3, sizeBaseX5, sizeBaseX10 },
    } = getGelTokens();
    const {
        screen,
        isXsScreen,
    } = useContext(GelScreenDetectorContext) as GelScreenDetectorContext;

    const downloadAttachment = (attachment: Attachment) => {
        const bytes = Uint8Array.from(atob(attachment.base64), c => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: attachment.mimeType });
        saveAs(blob, attachment.fileName);
    }

    const downloadAll = async () => {
        const zip = new JSZip();
        for (const attachment of attachments) {
            const bytes = Uint8Array.from(atob(attachment.base64), c => c.charCodeAt(0));
            zip.file(attachment.fileName, bytes, { binary: true });
        }
        const content = await zip.generateAsync({ type: "blob" });
        saveAs(content, `correspondence-${new Date(createdAt).toLocaleDateString()}.zip`);
    };

    const Rows = styled.div({
        padding: '32px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    });

    const Row = styled.div(() => ({
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        alignItems: 'center',
        width: '100%',
        // right-align the value
        '& > *:last-child': {
            justifySelf: 'end',
        },
    }));

    const formatDate = (date: string): string => {
        const d = new Date(date); // parses the ISO string

        // Local time (your machine’s timezone). Uses day/month/year ordering.
        const ddmmyyyy = new Intl.DateTimeFormat('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).format(d);

        return ddmmyyyy;
    }

    if (isLoading) return <GelSpinner medium overlay />;

    return (
        <GelContainerLite style={{ padding: sizeNone, overflow: "hidden" }}>
            <PageHeader >
                <GelHeading2>{data?.corroLink?.Files?.Heading}</GelHeading2>
                <GelParagraph large={true} style={{ paddingTop: sizeBaseX3 }}>
                    {data?.corroLink?.Files?.SubHeading}
                </GelParagraph>
            </PageHeader>

            <ResponsiveContainer>
                <GelRowLayout style={{ padding: isXsScreen(screen) ? `0px ${sizeBaseX5}px` : `0px ${sizeBaseX10}px` }}>
                    <GelParagraph style={{ 'paddingTop': '64px' }}>
                        {data?.corroLink?.Files?.Description}
                    </GelParagraph>

                    <Card title="Claim details" showFooter={false} style={{ margin: "32px 0" }}>
                        <Rows>
                            {details.map(({ label, value }) => (
                                <Row key={label}>
                                    <GelLabel>{label}</GelLabel>
                                    <GelParagraph>{value ?? "—"}</GelParagraph>
                                </Row>
                            ))}
                        </Rows>
                    </Card>

                    <Card title={"Documents"}
                        showFooter={false}
                        style={{ 'marginBottom': '32px' }}>
                        <div style={{
                            display: 'flex',
                            padding: '32px 16px',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: '16px',
                            alignSelf: 'stretch'
                        }}>
                            <div
                                style={{
                                    display: "flex",
                                    width: "100%",
                                    justifyContent: isXsScreen(screen) ? "center" : "flex-end",
                                }}
                            >
                                <GelButton primary medium onClick={downloadAll} iconRight="Download">
                                    Download All
                                </GelButton>
                            </div>

                            <HorizontalRule />

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                                {attachments.map((a, idx) => (
                                    <CorroFileItem
                                        key={idx}
                                        title={a.fileName}
                                        date={formatDate(createdAt)}
                                        onClick={() => downloadAttachment(a)} />
                                ))}

                                {attachments.length === 0 && <div style={{ position: "absolute", top: "30%", left: "50%", transform: "translate(-50%, -50%)", width: "100%" }}>
                                    <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                                        <GelHeading5 style={{ paddingTop: "10%", color: "#54544C" }}>
                                            {data?.corroLink?.Files?.NoFiles?.Message}
                                        </GelHeading5>
                                        <GelParagraph style={{ paddingTop: "8px", color: "#54544C", fontWeight: "400" }}>
                                            {data?.corroLink?.Files?.NoFiles?.Description}
                                        </GelParagraph>
                                    </div>
                                </div>}
                            </div>

                        </div>
                    </Card>

                    {data?.corroLink?.Files?.Contact && <InlineAlert message={data?.corroLink?.Files?.Contact} style={{ marginBottom: 32 }} />}

                </GelRowLayout>
            </ResponsiveContainer>
        </GelContainerLite>
    );
};

export default CorroAttachmentList;
